import { Component, Prop, h, Element } from '@stencil/core';

const COMPONENT_NAME = 'me-row';

/**
 * A flexible layout row component based on CSS Flexbox.
 *
 * This component renders a customizable flex container with support for:
 * - axis-based gaps (`xGap`, `yGap`)
 * - alignment (`align`, `justify`)
 * - flex direction and wrapping (`direction`, `wrap`)
 * - custom root tag (e.g., `div`, `section`)
 * - host-defined attributes (`id`, `data-*`, `aria-*`, etc.)
 * - custom classes via the standard `class` attribute
 */

const DEFAULTS = {
  tag: 'div',
  yGap: '4xl',
  yGapMd: '4xl',
  yGapLg: '6xl',
  xGap: 'lg',
  xGapMd: 'lg',
  xGapLg: '6xl',
  align: 'start',
  alignMd: 'start',
  alignLg: 'start',
  justify: 'start',
  justifyMd: 'start',
  justifyLg: 'start',
  wrap: 'wrap',
  wrapMd: 'wrap',
  wrapLg: 'wrap',
  direction: 'row',
  directionMd: 'row',
  directionLg: 'row',
} as const;

@Component({
  tag: 'me-row',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Row {
  /** Reference to the host element (<me-row>) */
  @Element() host: HTMLElement;

  /**
   * The HTML tag used for rendering the row container.
   * @default 'div'
   */
  @Prop() tag: 'div' | 'section' | 'nav' = DEFAULTS.tag;

  /**
   * Horizontal spacing between child elements (left/right).
   * Applied via a `me-row--x-gap-{value}` modifier class.
   */
  @Prop() xGap: 'none' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' = DEFAULTS.xGap;
  @Prop() xGapMd?: 'none' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl';
  @Prop() xGapLg: 'none' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' = DEFAULTS.xGapLg;

  /**
   * Vertical spacing between rows (top/bottom).
   * Applied via a `me-row--y-gap-{value}` modifier class.
   */
  @Prop() yGap: 'none' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' = DEFAULTS.yGap;
  @Prop() yGapMd?: 'none' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl';
  @Prop() yGapLg: 'none' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' = DEFAULTS.yGapLg;

  /**
   * Alignment of children along the cross axis.
   * Maps to the CSS `align-items` property.
   */
  @Prop() align: 'start' | 'center' | 'end' | 'stretch' = DEFAULTS.align;
  @Prop() alignMd?: 'start' | 'center' | 'end' | 'stretch';
  @Prop() alignLg: 'start' | 'center' | 'end' | 'stretch' = DEFAULTS.alignLg;

  /**
   * Alignment of children along the main axis.
   * Maps to the CSS `justify-content` property.
   */
  @Prop() justify: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly' = DEFAULTS.justify;
  @Prop() justifyMd?: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
  @Prop() justifyLg: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly' = DEFAULTS.justifyLg;

  /**
   * Controls the wrapping behavior of the flex container.
   * Maps to the CSS `flex-wrap` property.
   */
  @Prop() wrap: 'wrap' | 'no-wrap' = DEFAULTS.wrap;
  @Prop() wrapMd?: 'wrap' | 'no-wrap';
  @Prop() wrapLg: 'wrap' | 'no-wrap' = DEFAULTS.wrapLg;

  /**
   * Controls the direction of the flex container.
   * Maps to the CSS `flex-direction` property.
   */
  @Prop() direction: 'row' | 'row-reverse' | 'column' | 'column-reverse' = DEFAULTS.direction;
  @Prop() directionMd?: 'row' | 'row-reverse' | 'column' | 'column-reverse';
  @Prop() directionLg: 'row' | 'row-reverse' | 'column' | 'column-reverse' = DEFAULTS.directionLg;

  /**
   * Collects and merges all layout-related modifier classes
   * with any custom `class` from the host element.
   */
  private getMergedClasses(): string {
    const baseClasses = [
      COMPONENT_NAME,
      this.xGap ? `${COMPONENT_NAME}--x-gap-${this.xGap}` : null,
      this.xGapMd ? `${COMPONENT_NAME}--x-gap-md-${this.xGapMd}` : null,
      this.xGapLg ? `${COMPONENT_NAME}--x-gap-lg-${this.xGapLg}` : null,
      this.yGap ? `${COMPONENT_NAME}--y-gap-${this.yGap}` : null,
      this.yGapMd ? `${COMPONENT_NAME}--y-gap-md-${this.yGapMd}` : null,
      this.yGapLg ? `${COMPONENT_NAME}--y-gap-lg-${this.yGapLg}` : null,
      this.align ? `${COMPONENT_NAME}--align-${this.align}` : null,
      this.alignMd ? `${COMPONENT_NAME}--align-md-${this.alignMd}` : null,
      this.alignLg ? `${COMPONENT_NAME}--align-lg-${this.alignLg}` : null,
      this.justify ? `${COMPONENT_NAME}--justify-${this.justify}` : null,
      this.justifyMd ? `${COMPONENT_NAME}--justify-md-${this.justifyMd}` : null,
      this.justifyLg ? `${COMPONENT_NAME}--justify-lg-${this.justifyLg}` : null,
      this.wrap ? `${COMPONENT_NAME}--wrap-${this.wrap}` : null,
      this.wrapMd ? `${COMPONENT_NAME}--wrap-md-${this.wrapMd}` : null,
      this.wrapLg ? `${COMPONENT_NAME}--wrap-lg-${this.wrapLg}` : null,
      this.direction ? `${COMPONENT_NAME}--direction-${this.direction}` : null,
      this.directionMd ? `${COMPONENT_NAME}--direction-md-${this.directionMd}` : null,
      this.directionLg ? `${COMPONENT_NAME}--direction-lg-${this.directionLg}` : null,
    ];

    const hostClass = this.host.getAttribute('class') ?? '';

    return [...baseClasses.filter(Boolean), hostClass].join(' ').trim();
  }

  render() {
    const Tag = this.tag;
    const className = this.getMergedClasses();

    return (
      <Tag class={className}>
        <slot></slot>
      </Tag>
    );
  }
}
